// Agrega entradas ai_run_meta do auditLogStore para painel de precisão.
// Função pura — recebe array de log entries, retorna métricas agregadas.

function parseDetail(entry) {
  if (!entry || entry.action !== "ai_run_meta") return null;
  try {
    return typeof entry.detail === "string" ? JSON.parse(entry.detail) : entry.detail;
  } catch {
    return null;
  }
}

function avg(nums) {
  const valid = nums.filter(n => Number.isFinite(n));
  if (valid.length === 0) return null;
  return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length);
}

function increment(map, key) {
  map[key] = (map[key] || 0) + 1;
}

/**
 * Agrega telemetria de runs de IA. Filtra só entries com action="ai_run_meta".
 *
 * @param {Array} logEntries — log do auditLogStore
 * @returns {{
 *   totalRuns: number,
 *   escalatedCount: number,
 *   escalationRate: number,             // 0..1
 *   avgLatencyMs: number|null,
 *   avgLatencyEscalatedMs: number|null,
 *   avgLatencyNonEscalatedMs: number|null,
 *   signalFrequency: Record<string, number>,
 *   byModule: Record<string, { runs, escalated, rate }>,
 *   byModel: Record<string, number>,
 * }}
 */
export function aggregatePrecisionTelemetry(logEntries) {
  const empty = {
    totalRuns: 0,
    escalatedCount: 0,
    escalationRate: 0,
    avgLatencyMs: null,
    avgLatencyEscalatedMs: null,
    avgLatencyNonEscalatedMs: null,
    signalFrequency: {},
    byModule: {},
    byModel: {},
    truncatedCount: 0,
    truncationRate: 0,
    avgOutputTokens: null,
    avgContextChars: null,
    avgInputTokens: null,
    cacheHitRate: null,
  };
  if (!Array.isArray(logEntries) || logEntries.length === 0) return empty;

  const runs = [];
  for (const entry of logEntries) {
    const detail = parseDetail(entry);
    if (detail) runs.push({ entry, detail });
  }

  if (runs.length === 0) return empty;

  let escalatedCount = 0;
  let truncatedCount = 0;
  const latAll = [];
  const latEsc = [];
  const latNon = [];
  const outTokens = [];
  const ctxChars = [];
  const inTokens = [];
  let cacheReadSum = 0;
  let cacheCreatedSum = 0;
  let cacheBaseSum = 0;
  const signalFrequency = {};
  const byModule = {};
  const byModel = {};

  for (const { entry, detail } of runs) {
    const escalated = Boolean(detail.escalated);
    if (escalated) escalatedCount += 1;
    if (detail.truncated) truncatedCount += 1;

    if (Number.isFinite(detail.latency_ms)) {
      latAll.push(detail.latency_ms);
      (escalated ? latEsc : latNon).push(detail.latency_ms);
    }
    if (Number.isFinite(detail.output_tokens)) outTokens.push(detail.output_tokens);
    if (Number.isFinite(detail.context_chars)) ctxChars.push(detail.context_chars);
    if (Number.isFinite(detail.input_tokens)) inTokens.push(detail.input_tokens);
    const cr = Number.isFinite(detail.cache_read_tokens) ? detail.cache_read_tokens : 0;
    const cc = Number.isFinite(detail.cache_creation_tokens) ? detail.cache_creation_tokens : 0;
    const it = Number.isFinite(detail.input_tokens) ? detail.input_tokens : 0;
    if (cr || cc || it) {
      cacheReadSum += cr;
      cacheCreatedSum += cc;
      cacheBaseSum += cr + cc + it;
    }

    if (Array.isArray(detail.escalation_signals)) {
      for (const sig of detail.escalation_signals) increment(signalFrequency, sig);
    }

    const mod = entry.module || "unknown";
    if (!byModule[mod]) byModule[mod] = { runs: 0, escalated: 0, rate: 0 };
    byModule[mod].runs += 1;
    if (escalated) byModule[mod].escalated += 1;

    const model = detail.escalated_to || detail.model || "unknown";
    increment(byModel, model);
  }

  for (const k of Object.keys(byModule)) {
    const m = byModule[k];
    m.rate = m.runs > 0 ? m.escalated / m.runs : 0;
  }

  return {
    totalRuns: runs.length,
    escalatedCount,
    escalationRate: runs.length > 0 ? escalatedCount / runs.length : 0,
    avgLatencyMs: avg(latAll),
    avgLatencyEscalatedMs: avg(latEsc),
    avgLatencyNonEscalatedMs: avg(latNon),
    signalFrequency,
    byModule,
    byModel,
    truncatedCount,
    truncationRate: runs.length > 0 ? truncatedCount / runs.length : 0,
    avgOutputTokens: avg(outTokens),
    avgContextChars: avg(ctxChars),
    avgInputTokens: avg(inTokens),
    cacheHitRate: cacheBaseSum > 0 ? cacheReadSum / cacheBaseSum : null,
  };
}
